
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { getLocale, shallowEqual, useIntl, useSelector } from 'umi';
import moment from 'moment';
import CardBox from "@/pages/dashboard/components/CardBox";
import BarChart from "@/pages/dashboard/components/Chart/components/BarChart";
import LineChart from "@/pages/dashboard/components/Chart/components/LineChart";
import MiniTable from "@/pages/dashboard/components/MiniTable";
import { addChartLabel, xAxisHighLight, xAxisLabelFormat } from "@/pages/dashboard/components/Chart/_config/plug";
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import { handleDownload, commonValueFormatter, commonDownloadValueFormatter, formatSmallValue, formatPercentNum, unitConversion } from "@/pages/dashboard/utils/utils";
import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble';
import { useWeekDay } from '@/pages/dashboard/utils/customHook';
import { MetricsTrenProps } from './types';
import './index.scss';

const MetricsTrendCo = (props: MetricsTrenProps) => {
  const { className = '', style = {}, config, metricData, loading, error } = props;
  const {
    componentMetrics = [],
    titleEn,
    titleCn,
    height = 340,
    bordered = false,
    defaultAvtive = 2,
    showConfig,
    setIconType,
    setOtherConfigFunc,
    formatterFunc: toolTipFunc
  } = config;
  const { showEvent, showEye, showBarChart, showLineChart, showTable, showDownloadTable } = showConfig || {
    showEvent: false,
    showEye: true,
    showBarChart: true,
    showLineChart: true,
    showTable: true,
    showDownloadTable: true,
  };

  const { getWeekDay } = useWeekDay();
  const { formatMessage } = useIntl(); // 中英切换方法
  const isEN = getLocale() === 'en-US';
  const contextObj = useContext(FilterContext);
  const { filterLoading, filterVal = {} } = contextObj || {};
  const { timeType = 'Day' } = filterVal || {};
  const title = isEN ? titleEn : titleCn;
  const [iseye, setIseye] = useState(true);
  const [isEvent, setIsEvent] = useState(true);

  useEffect(() => {
    setIsEvent(true);
    setIseye(true);
  },[filterVal]);

  const handleAxisFormat = (value: moment.MomentInput) => {
    return timeType === "Month" ? moment(value).format('YYYY-MM') : moment(value).format('MM-DD');
  };

  const { xAxisData = [], legendData = [], barSeries = [], lineSeries = [], isNodata = true, table = { option: [], data: [], downloadData: [] } } = useMemo(() => {
    const xAxisData: any[] = metricData?.map((item: any) => item?.date) || [];
    const legendData: any[] = [];
    const barSeries: any[] = [];
    const lineSeries: any[] = [];
    const tableData: any[] = [];
    const tableDownloadData: any[] = [];
    let isNodata: boolean = true;
    const tableOption: any[] = [
      {
        key: 'date',
        header: formatMessage({ id: 'overview_table_date' }),
        align: 'left',
        fixed: 'left',
        width: 160,
        render: (cvm: any) => {
          const weekDay = getWeekDay(cvm?.date);
          const isSunOrSat = weekDay === 'Sun' || weekDay === 'Sat' || weekDay === '周日' || weekDay === '周六';
          return <>
            <span className='table-text'>{cvm?.date}</span>
            {timeType !== 'Month' && <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>}
          </>;
        },
      }
    ];
    // 趋势图
    if (showBarChart || showLineChart) {
      componentMetrics?.map((metric: any) => {
        const { metricKey, reqMetricKey, type, decimal, enName, cnName } = metric || {};
        const metricName = isEN ? enName : cnName;
        const seriesObj: any = {
          name: metricName,
          data: [],
        };
        metricData?.map((item: any) => {
          // 兼容传参跟后端返回值不一致的情况
          const value = reqMetricKey ? item?.metrics?.[reqMetricKey]?.value : item?.metrics?.[metricKey]?.value;
          seriesObj.data?.push(value || '');
          if (value) isNodata = false;
        });
        legendData?.push(metricName);
        showBarChart && barSeries?.push({
          type: 'bar',
          barGap: '5%',
          ...seriesObj
        });
        showLineChart && lineSeries?.push({
          type: 'line',
          symbol: 'circle',
          ...seriesObj
        });
        if (showTable || showDownloadTable) {
          tableOption?.push({
            key: metricKey,
            header: metricName,
            align: 'right',
          });
        }
      });
    }
    // 表格
    if (showTable || showDownloadTable) {
      metricData?.map((item: any) => {
        const date = timeType === 'Month' ? moment(item?.date).format('YYYY-MM') : moment(item?.date).format('YYYY-MM-DD');
        const tableObj: any = { date };
        const downTableObj: any = { date };
        componentMetrics?.map((metric: any) => {
          const { metricKey, reqMetricKey, type, decimal, enName, cnName } = metric || {};
          const value = reqMetricKey ? item?.metrics?.[reqMetricKey]?.value : item?.metrics?.[metricKey]?.value;
          tableObj[metricKey] = commonValueFormatter(type, value, decimal);
          downTableObj[metricKey] = commonDownloadValueFormatter(type, value, decimal);
        });
        tableData?.push(tableObj);
        tableDownloadData?.push(downTableObj);
      });
    }

    return {
      xAxisData,
      legendData,
      barSeries,
      lineSeries,
      isNodata,
      table: {
        option: tableOption,
        data: tableData,
        downloadData: tableDownloadData
      }
    };
  }, [metricData, showConfig, isEN]);

  const formatterFunc = (params: any) => {
    const date = timeType === 'Month' ? moment(params?.[0]?.axisValue).format('YYYY-MM') : moment(params?.[0]?.axisValue).format('YYYY-MM-DD');
    const data = params?.map((item: { seriesName: any; value: number; color: any; }, index: number) => {
      const { metricKey, type, decimal } = componentMetrics?.[index] || {};
      return {
        name: item.seriesName,
        metricsNum: metricKey === 'arpu' && item?.value < 1 && item?.value > 0 ? formatSmallValue(item?.value, 'decimals', decimal) : commonValueFormatter(type, item?.value, decimal),
        color: item.color
      };
    });
    return getEchartTooltipText({
      title: date,
      data,
    });
  };

  const yAxisLabelFormat = (value: any) => {
    const formatLabel = value ? unitConversion(value, true) : value;
    const num = value < 1 && value > 0 ? parseFloat(value.toFixed(3)) : formatLabel;
    return num;
  };

  const configFunc = (opt: any) => {
    opt = xAxisLabelFormat(opt, { xAxisLabelFormat: handleAxisFormat });
    const { metricKey, type, decimal } = componentMetrics?.[0] || {};
    opt = addChartLabel(opt, {
      formatter: (params: any) => {
        return metricKey === 'arpu' && params?.value < 1 && params?.value > 0 ? formatSmallValue(params?.value, 'decimals', decimal) : commonValueFormatter(type, params?.value, decimal);
      },
      show: iseye,
      formatNum: (value: any) => {
        return metricKey === 'arpu' && value < 1 && value > 0 ? formatSmallValue(value, 'decimals', decimal) : commonValueFormatter(type, value, decimal);
      }
    });
    opt = xAxisHighLight(opt, {
      formatter: (params: { value: string; }) => {
        return handleAxisFormat(params?.value);
      }
    });
    if (type === 'percent') {
      opt.yAxis.axisLabel.formatter = (value: number) => {
        return formatPercentNum(value, 2);
      };
    }
    if (metricKey === 'arpu') {
      opt.yAxis.axisLabel.formatter = (value: number) => {
        if (value < 1 && value > 0) return formatSmallValue(value, 'decimals', 2);
        return yAxisLabelFormat(value);
      };
    }
    if (setOtherConfigFunc) opt = setOtherConfigFunc(opt, { iseye, isEvent });
    return opt;
  };

  const renderList = [
    showEvent && {
      type: 'event',
      svgType: isEvent ? 'move-left' : 'move-right',
      content: isEvent ? formatMessage({ id: 'common_show_event' }) : formatMessage({ id: 'common_hide_event' }),
      isHideCom: true,
    },
    showEye && {
      type: 'eyes',
      svgType: iseye ? 'open-eyes' : 'eyes-closed',
      content: iseye ? formatMessage({ id: 'common_show_values' }) : formatMessage({ id: 'common_hide_values' }),
      isHideCom: true,
    },
    showBarChart && {
      type: 'barChart',
      svgType: 'column-chart',
      content: formatMessage({ id: 'common_bar_chart' }),
      component: <BarChart
        height={height}
        legendData={xAxisData?.length > 0 ? legendData : []}
        xAxisData={xAxisData || []}
        series={xAxisData?.length > 0 ? barSeries : []}
        formatterFunc={toolTipFunc ? (params: any) => toolTipFunc(params, componentMetrics) : formatterFunc}
        setOtherConfigFunc={(opt: any) => {
          opt = configFunc(opt);
          return opt;
        }}
      />
    },
    showLineChart && {
      type: 'lineChart',
      svgType: 'discount-chart',
      content: formatMessage({ id: 'common_line_chart' }),
      component: <LineChart
        height={height}
        legendData={xAxisData?.length > 0 ? legendData : []}
        xAxisData={xAxisData || []}
        series={xAxisData?.length > 0 ? lineSeries : []}
        formatterFunc={toolTipFunc ? (params: any) => toolTipFunc(params, componentMetrics) : formatterFunc}
        setOtherConfigFunc={(opt: any) => {
          opt = configFunc(opt);
          return opt;
        }}
      />
    },
    showTable && {
      type: 'sheet',
      svgType: 'sheet',
      content: formatMessage({ id: 'common_table' }),
      component: <MiniTable
        height={height}
        loading={false}
        tableOption={table?.option}
        tableList={table?.data}
      />
    }
  ]?.filter(v => v !== false);

  return <CardBox
    className={`register-table ${className}`}
    style={style}
    titleCom={title}
    height={height}
    bordered={bordered}
    renderList={renderList}
    defaultAvtive={defaultAvtive}
    setCurrentDataType={(type: any) => {
      if (type === 'eyes') {
        setIseye(!iseye);
      }
      if (type === 'event') {
        setIsEvent(!isEvent);
      }
      if (setIconType) {
        setIconType(type);
      }
    }}
    downloadClick={async () => {
      handleDownload(table, title);
    }}
    isLoading={loading}
    isError={error}
    isNodata={isNodata}
  />;
};

export default MetricsTrendCo;